/**
 * What the server said when a SEND was refused, in words an agent can act on.
 *
 * ★ EXTRACTED BECAUSE A SECOND SENDER APPEARED.
 * This lived inside Composer.jsx while it was the only thing that sent a
 * message. The template button sends too, and it must say the same words for
 * the same refusal — two copies of a vocabulary like this drift the moment one
 * of them gains a code the other lacks.
 *
 * Its sibling `startErrors.js` covers a different question — why a
 * conversation could not be STARTED — and the two vocabularies are deliberately
 * separate: "you are not assigned to this number" is a different sentence from
 * "you are no longer assigned to this number".
 */

/** Map a server refusal onto something an agent can act on. */
const REASON_TEXT = {
	// Canonical ownership: one external thread, one person replying.
	CONVERSATION_UNCLAIMED:
		'Nobody has taken this conversation yet. Claim it to reply.',
	NOT_CONVERSATION_OWNER:
		'Someone else is handling this conversation. Ask them to transfer it to you.',
	SESSION_EXPIRED_TEMPLATE_REQUIRED:
		'This conversation is outside the 24-hour window. Send an approved template to reopen it.',
	CHANNEL_NOT_ASSIGNED: 'You are no longer assigned to this number.',
	CHANNEL_DISABLED: 'This number has been disabled.',
	TOKEN_NEEDS_REAUTH: 'WhatsApp needs reconnecting. Ask an administrator.',
	ACCOUNT_RESTRICTED: 'Sending is restricted on this account right now.',
	LEAD_OUT_OF_SCOPE: 'You no longer have access to this conversation.',
	// The bound number cannot send at all. The connection layer names the
	// blocker verbatim (the channel keeps them separately on purpose), so each
	// gets its own sentence rather than one vague "not sendable".
	CHANNEL_NOT_SENDABLE: "This number can't send messages right now. Ask an administrator.",
	metaDisconnected: 'This number has been disconnected from WhatsApp. Ask an administrator.',
	tokenNeedsReauth: 'WhatsApp needs reconnecting. Ask an administrator.',
	accountRestricted: 'Sending is restricted on this account right now.',
	healthDegraded: 'This number is currently limited by WhatsApp. Try again later.',
	account: 'This WhatsApp account is not active. Ask an administrator.',
	SEND_UNAVAILABLE: 'Messaging is unavailable right now. Try again in a moment.',
	CONVERSATION_HAS_NO_CHANNEL: 'This conversation is not linked to a number.',
	// ===== Template sends =====
	// Every one of these means the picker and the server disagree about the
	// catalogue, so each sentence ends in the thing that resolves it — usually
	// refreshing the list — rather than sending the agent to an administrator
	// for something they can fix themselves.
	TEMPLATE_NOT_FOUND: 'That template is no longer available on this number. Refresh the list and pick another.',
	TEMPLATE_NOT_APPROVED: 'That template is not approved for sending right now. Refresh the list and pick another.',
	TEMPLATE_PARAMS_INVALID: 'Some values do not fit this template. Check the highlighted fields.',
	TEMPLATE_HEADER_UNSUPPORTED: 'This template needs an image or file in its header, which cannot be sent from the inbox yet.',
	CHANNEL_HAS_NO_WABA: 'This number is not finished setting up. Ask an administrator.',
	// These three mean the CLIENT sent something it should never send. An agent
	// can do nothing about any of them, so they say so plainly instead of
	// suggesting a retry that would fail identically.
	TEMPLATE_COMPONENTS_NOT_ACCEPTED: 'This template could not be sent. Reload the page and try again.',
	TEMPLATE_ON_NON_TEMPLATE_SEND: 'This template could not be sent. Reload the page and try again.',
	TEMPLATE_NAME_REQUIRED: 'No template was chosen.',
	// Attachment refusals that reach here by way of the send mutation. They are
	// deployment or credential problems wearing a file's clothes, so they must
	// not read as "your photo was bad" — see useOutboundMedia for the same
	// vocabulary on the upload leg itself.
	MEDIA_UPLOAD_FAILED: 'WhatsApp rejected this file. Ask an administrator to check the connection.',
	UPLOAD_FAILED: 'WhatsApp rejected this file. Ask an administrator to check the connection.',
	MEDIA_UPLOAD_UNSUPPORTED: 'Attachments are not enabled on this workspace yet.',
	// The connection layer could not read the request body at all — it is
	// running a build without the attachment support this needs.
	RAW_BODY_UNAVAILABLE: 'Attachments are not enabled on this workspace yet.',
};

/**
 * The most specific thing the server said, in that order: a resolver reason,
 * a channel blocker, then the error code itself — before falling back to the
 * server's own message (Meta codes are translated there, in one place).
 */
const explainRefusal = (payload = {}) =>
	REASON_TEXT[payload.reason] ??
	REASON_TEXT[payload.blocker] ??
	REASON_TEXT[payload.error] ??
	payload.message ??
	'The message could not be sent.';

export { REASON_TEXT, explainRefusal };
